import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Building2, Loader2 } from "lucide-react";

const B2BWaitlistModal = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-b2b-waitlist", handler);
    return () => window.removeEventListener("open-b2b-waitlist", handler);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from("b2b_waitlist" as any).insert({
        email: email.trim(),
        company_name: company.trim() || null,
      });
      if (error) throw error;
      // Send notification email to contact@greyrockai.app
      try {
        await supabase.functions.invoke("send-b2b-notification", {
          body: { email: email.trim(), company_name: company.trim() || null },
        });
      } catch (notifErr) {
        console.error("B2B notification email failed:", notifErr);
      }
      toast.success(`Thank you. A GreyRock Enterprise specialist will reach out to you at ${email.trim()} shortly.`);
      setOpen(false);
      setEmail("");
      setCompany("");
    } catch {
      toast.error("Failed to join waitlist. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-charcoal-dark border-glass-border max-w-sm">
        <DialogHeader>
          <div className="flex justify-center mb-3">
            <div className="w-12 h-12 rounded-full bg-cyan/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-cyan" />
            </div>
          </div>
          <DialogTitle className="text-center text-lg font-bold text-foreground">
            GreyRock for Teams & API
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground text-center">
          Enterprise-grade behavioral intelligence for HR, legal teams, and platforms.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <input
            type="email"
            required
            placeholder="Work email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={cn(
              "w-full px-4 py-2.5 rounded-xl text-sm",
              "bg-charcoal-medium border border-glass-border text-foreground",
              "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cyan/50"
            )}
          />
          <input
            type="text"
            placeholder="Company name"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className={cn(
              "w-full px-4 py-2.5 rounded-xl text-sm",
              "bg-charcoal-medium border border-glass-border text-foreground",
              "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cyan/50"
            )}
          />
          <button
            type="submit"
            disabled={submitting}
            className={cn(
              "w-full py-2.5 rounded-xl font-semibold text-sm",
              "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
              "hover:scale-[1.01] transition-transform",
              "disabled:opacity-50"
            )}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Join the Waitlist"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default B2BWaitlistModal;
